import { useEffect } from 'react';
import { Stack, Group, Divider, ScrollArea, Anchor } from '@mantine/core';
import { useSchemaForm } from '@ayocore/platform/client/hooks/useSchemaForm';
import taskSchemaDefinition, { type Task } from '@ayocore/tasks/schemas/task.schema';
import {
  taskRepository,
  taskTypeRepository,
  taskStatusRepository,
} from '@ayocore/tasks/client/rpc';
import { UserSelect } from '@ayocore/platform/identity/client/components/UserSelect';
import { useAuthController } from '@ayocore/platform/identity/client/useAuthController';

export type EntityType =
  | 'crm::LeadRepository'
  | 'crm::PartnerRepository'
  | 'crm::ContactPersonRepository'
  | 'crm::DealRepository'
  // Подзадачи проекта: задача, повешенная на задачу-проект (тред Activity)
  | 'tasks::TaskRepository';

export const TASK_ENTITY_TYPES: readonly EntityType[] = [
  'crm::LeadRepository',
  'crm::PartnerRepository',
  'crm::ContactPersonRepository',
  'crm::DealRepository',
  'tasks::TaskRepository',
];

export function isTaskEntityType(value: unknown): value is EntityType {
  return typeof value === 'string' && (TASK_ENTITY_TYPES as readonly string[]).includes(value);
}

export function CrmCreateTaskForm({
  entityUuid,
  entityType,
  onCreated,
}: {
  entityUuid: string;
  entityType: EntityType;
  onCreated?: () => void;
}) {
  const SchemaForm = useSchemaForm<Task>(taskSchemaDefinition.createSchema, {
    hideDescriptions: true,
  });
  const currentUser = useAuthController(s => s.user);
  const taskTypeId = SchemaForm.values.task_type_id;

  // Prefill defaults on mount
  useEffect(() => {
    const init = async () => {
      // Find default status "Новая"
      const statuses = await taskStatusRepository.search('Новая').limit(1).fetch();
      const defaultStatusId = statuses?.[0]?.id;

      // Find default type "Задача"
      const types = await taskTypeRepository.search('Задача').limit(1).fetch();
      const defaultTypeId = types?.[0]?.id;

      SchemaForm.setValues({
        entity_uuid: entityUuid,
        entity_type: entityType,
        task_status_id: defaultStatusId ?? undefined,
        assignee_id: currentUser?.id ?? undefined,
        task_type_id: defaultTypeId ?? undefined,
      });
    };
    void init();
  }, [entityUuid, entityType]);

  // React to task type changes: filter statuses and set default first active by sort_order
  useEffect(() => {
    const applyDefaultStatus = async () => {
      if (!taskTypeId) return;
      const statuses = await taskStatusRepository
        .where('task_type_id', taskTypeId)
        .where('is_active', true)
        .orderBy('sort_order', 'asc')
        .fetch();
      const first = statuses[0];
      if (first) {
        SchemaForm.setFieldValue('task_status_id', first.id);
      }
    };
    applyDefaultStatus();
  }, [taskTypeId]);

  const handleSubmit = async (values: Task) => {
    await taskRepository.create(values);
    SchemaForm.setSuccess('Задача успешно создана!');
    setTimeout(() => onCreated?.(), 800);
    return { success: true };
  };

  const applyDatePreset = (daysFromNow: number) => {
    const now = new Date();
    // UTC-полдень: локальная дата пользователя + daysFromNow, без day-drift при сохранении
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() + daysFromNow, 12, 0, 0, 0));
    SchemaForm.setValues({ due_date: d });
  };

  const applyTimePreset = (hh: string, mm: string) => {
    SchemaForm.setValues({ due_time: `${hh}:${mm}:00` });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '80dvh' }}>
      <ScrollArea style={{ flex: 1 }} scrollHideDelay={0}>
        <form onSubmit={SchemaForm.handleSubmit(handleSubmit)}>
            <Stack gap='xs' pb='md'>
              <SchemaForm.Messages />

              {/* Basic */}
              <SchemaForm.Field.title />

              <SchemaForm.Field.description autosize minRows={4} maxRows={12} />

              <Divider />

              {/* Status / Assignee */}
              <UserSelect
                label='Ответственный'
                placeholder='Выберите исполнителя...'
                value={SchemaForm.values.assignee_id ?? null}
                onChange={id => SchemaForm.setFieldValue('assignee_id', id)}
              />

              <Divider />

              <Group grow>
                <Stack gap={4}>
                  <SchemaForm.Field.due_date />
                  <Group gap='xs'>
                    <Anchor size='xs' onClick={() => applyDatePreset(1)}>
                      Завтра
                    </Anchor>
                    <Anchor size='xs' onClick={() => applyDatePreset(2)}>
                      Послезавтра
                    </Anchor>
                    <Anchor size='xs' onClick={() => applyDatePreset(7)}>
                      Через неделю
                    </Anchor>
                  </Group>
                </Stack>
                <Stack gap={4}>
                  <SchemaForm.Field.due_time />
                  <Group gap='xs'>
                    <Anchor size='xs' onClick={() => applyTimePreset('08', '00')}>
                      8:00
                    </Anchor>
                    <Anchor size='xs' onClick={() => applyTimePreset('10', '00')}>
                      10:00
                    </Anchor>
                    <Anchor size='xs' onClick={() => applyTimePreset('12', '00')}>
                      12:00
                    </Anchor>
                    <Anchor size='xs' onClick={() => applyTimePreset('14', '00')}>
                      14:00
                    </Anchor>
                    <Anchor size='xs' onClick={() => applyTimePreset('16', '00')}>
                      16:00
                    </Anchor>
                    <Anchor size='xs' onClick={() => applyTimePreset('18', '00')}>
                      18:00
                    </Anchor>
                  </Group>
                </Stack>
              </Group>

              {/* Hidden fields (not rendered) */}
              {/* parent_task_id intentionally omitted */}
              {/* entity_uuid / entity_type prefilled and hidden */}
            </Stack>

            {/* Sticky footer */}
            <div
              style={{
                borderTop: '1px solid #e9ecef',
                backgroundColor: 'transparent',
                padding: '8px 0',
                flexShrink: 0,
                position: 'sticky',
                bottom: 0,
              }}
            >
              <Group justify='space-between'>
                <SchemaForm.buttons.cancel label='Отмена' onClick={() => onCreated?.()} />
                <SchemaForm.buttons.submit label='Создать задачу' />
              </Group>
            </div>
        </form>
      </ScrollArea>
    </div>
  );
}

export default CrmCreateTaskForm;
